/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/components/ui/tooltip";
import { Send, X, Loader2, ChevronLeft, ChevronRight, GripVertical, Bot, Settings, Plus, Square } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@shared/components/ui/collapsible";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { listAgents } from "@shared/lib/api/agents";
import { getUserSettings } from "@shared/lib/api/user_settings";
import { useChatContext, ChatMessage } from "@shared/contexts/ChatContext";
import { PlanList } from "./PlanList";
import { ToolCallsList } from "./ToolCallsList";
import { EntityLinksDisplay } from "./EntityLinksDisplay";
import { SubAgentCallsList } from "./SubAgentCallsList";
import { ChatPlanDisplay } from "./ChatPlanDisplay";
import { ChatSettingsModal } from "./ChatSettingsModal";
import { FileUpload, isFileAttachmentSupported } from "@shared/components/ui/file-upload";
import { toast } from "@shared/components/ui/use-toast";
import { Agent } from '@shared/schema/agents';
import { MarkdownRenderer } from "@shared/components/ui/markdown-renderer";

interface AgentSidebarProps {
  className?: string;
  /** Optional navigation function for entity links (e.g., from React Router's useNavigate or wouter) */
  onNavigate?: (path: string) => void;
}

export function AgentSidebar({ className, onNavigate }: AgentSidebarProps) {
  const { state, 
          dispatch,
          setExpanded,
          setSidebarWidth,
          setSelectedAgent,
          addMessage,
          initializeHoncho,
          setIsLoading,
          resetChat,
          executeAgentWithStreaming,
          terminateCurrentExecution,
          startNewSession } = useChatContext();

  const [inputValue, setInputValue] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Get selected agent from context, default to 'product_manager' if not set
  const selectedAgent = state.selectedAgent || 'product_manager';

  // Fetch agents using React Query (same cache as agents page)
  const { data: agentsData, isLoading: isLoadingAgents, error: agentsError } = useQuery({
    queryKey: ['agents'],
    queryFn: () => listAgents(),
  });

  // Fetch user settings to get sidebar preferences
  const { data: userSettings, isLoading: isLoadingSettings } = useQuery(getUserSettings);

  // Handle errors with useEffect instead of onError (which is deprecated)
  React.useEffect(() => {
    if (agentsError) {
      console.error('Error loading agents:', agentsError);
      toast({
        title: "Error loading agents",
        description: "Could not load available agents. Please try refreshing the page.",
        variant: "destructive"
      });
    }
  }, [agentsError]);

  // Filter agents for sidebar based on user preferences
  const sidebarAgents = React.useMemo(() => {
    if (!agentsData?.agents) return [];
    
    console.log('🔍 [AgentSidebar] Raw agents data:', {
      totalAgents: agentsData.agents.length,
      agentIds: agentsData.agents.map(a => a.id),
      duplicateIds: agentsData.agents.map(a => a.id).filter((id, index, arr) => arr.indexOf(id) !== index)
    });
    
    // If user settings are not loaded yet, show all agents that have isEnabledInSidebar = true
    if (!userSettings) {
      const filtered = agentsData.agents.filter(agent => agent.isEnabledInSidebar);
      // Deduplicate by agent ID
      const deduplicated = Array.from(new Map(filtered.map(agent => [agent.id, agent])).values());
      console.log('🔍 [AgentSidebar] Filtered (no settings):', {
        beforeDedup: filtered.length,
        afterDedup: deduplicated.length,
        ids: deduplicated.map(a => a.id)
      });
      return deduplicated;
    }
    
    const agentSettings = (userSettings.agentSettings as any) || {};
    const sidebarVisibility = agentSettings.sidebarVisibility || {};
    
    const filtered = agentsData.agents.filter(agent => {
      // If user has set a preference, use that; otherwise use agent's default
      // Convert agent.id to string since object keys in JSON are always strings
      const agentIdKey = String(agent.id);
      const isEnabledInSidebar = sidebarVisibility.hasOwnProperty(agentIdKey) 
        ? sidebarVisibility[agentIdKey] 
        : agent.isEnabledInSidebar;
      return isEnabledInSidebar;
    });
    
    // Deduplicate by agent ID to prevent duplicate key warnings
    const deduplicated = Array.from(new Map(filtered.map(agent => [agent.id, agent])).values());
    console.log('🔍 [AgentSidebar] Filtered (with settings):', {
      beforeDedup: filtered.length,
      afterDedup: deduplicated.length,
      ids: deduplicated.map(a => a.id)
    });
    return deduplicated;
  }, [agentsData?.agents, userSettings]);

  // Smart auto-scroll: only scroll to bottom if user is already near the bottom
  useEffect(() => {
    // Use a small timeout to ensure DOM has updated after message changes
    const timeoutId = setTimeout(() => {
      const scrollArea = scrollAreaRef.current;
      if (!scrollArea) return;

      // ScrollArea from shadcn uses a nested structure, find the actual scrollable element
      const scrollableElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
      if (!scrollableElement) return;

      // Check if user is near the bottom (within 100px)
      const isNearBottom = scrollableElement.scrollTop + scrollableElement.clientHeight >= scrollableElement.scrollHeight - 100;
      
      // Only auto-scroll if user is near the bottom
      if (isNearBottom) {
        scrollToBottom();
      }
    }, 10);

    return () => clearTimeout(timeoutId);
  }, [state.messages]);

  // Scroll to bottom when sidebar expands, but only if there are messages
  // (more than just the initial welcome message)
  useEffect(() => {
    if (state.isExpanded && state.messages.length > 1) {
      scrollToBottom();
    }
  }, [state.isExpanded, state.messages.length]);

  // Initialize Honcho when component mounts (only if not already initialized)
  useEffect(() => {
    if (!state.isInitialized) {
      initializeHoncho();
    }
  }, []); // Empty dependency array - only run once on mount

  // Show welcome message only when no session exists and no messages
  const showWelcomeMessage = !state.dbSessionId && state.messages.length === 0 && !state.isLoading;
  
  // Get the current agent to determine the welcome message
  const currentAgent = sidebarAgents.find(agent => agent.name === selectedAgent);
  
  // Use agent's welcome message if specified, otherwise generate a dynamic one
  const welcomeMessageContent = currentAgent?.welcomeMessage 
    ?? `Hello! I'm the ${currentAgent?.displayName || 'Assistant'} agent. How can I help you today?`;
  
  const welcomeMessage: ChatMessage = {
    id: 'welcome',
    role: 'assistant',
    content: welcomeMessageContent,
    timestamp: new Date()
  };

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeRef.current) return;
    
    // Get the sidebar container (the collapsible content)
    const sidebarElement = resizeRef.current.closest('[data-state]'); // CollapsibleContent
    if (!sidebarElement) return;
    
    const sidebarRect = sidebarElement.getBoundingClientRect();
    
    // Calculate width based on mouse position relative to the sidebar's left edge
    const newWidth = e.clientX - sidebarRect.left;
    
    if (newWidth >= 280 && newWidth <= 600) {
      setSidebarWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const scrollToBottom = () => {
    // Commenting this out temporarily while we debug agent sidebar in the product app
    //messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (file: File | null) => {
    if (file && !isFileAttachmentSupported(file)) {
      toast({
        title: "Unsupported file type",
        description: "Please select a PDF, TXT, MD, CSV, JSON, or XML file.",
        variant: "destructive"
      });
      return;
    }
    setSelectedFile(file);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '' || state.isLoading || state.isExecuting) return;

    const userRequest = inputValue.trim();
    const attachedFile = selectedFile;
    
    // Clear inputs
    setInputValue('');
    setSelectedFile(null);

    try {
      // Execute the selected agent with streaming
      await executeAgentWithStreaming(selectedAgent, userRequest, attachedFile || undefined);
    } catch (error) {
      console.error('Error communicating with agent:', error);
      // Error handling is done within executeAgentWithStreaming
    }
  };

  const handleNewChat = async () => {
    try {
      await resetChat(); // Clear current session - new session will be created when user sends first message
      toast({
        title: "New Chat Started", 
        description: "Ready for a new conversation",
      });
    } catch (error) {
      console.error('Error starting new chat:', error);
      toast({
        title: "Error",
        description: "Failed to start new chat session. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleStopExecution = async () => {
    try {
      const success = await terminateCurrentExecution('User requested stop');
      if (success) {
        toast({
          title: "Execution Stopped",
          description: "Agent execution has been terminated.",
        });
      } else {
        toast({
          title: "Stop Failed",
          description: "Could not stop the current execution. It may have already completed.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error stopping execution:', error);
      toast({
        title: "Error",
        description: "Failed to stop execution. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className={cn("h-full relative", !state.isExpanded && "w-0", className)} data-agent-sidebar>
      <Collapsible
        open={state.isExpanded}
        onOpenChange={setExpanded}
        className="h-full relative"
      >
        <div className="absolute -right-4 z-10" data-agent-sidebar-expand-button>
          <CollapsibleTrigger asChild>
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8 rounded-full border border-slate-200 bg-white shadow-sm hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-700 dark:hover:bg-slate-700"
            >
              {state.isExpanded ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
        </div>
        
        <CollapsibleContent 
          forceMount
          className={cn(
            "transition-all duration-300 h-full overflow-hidden relative",
            state.isExpanded ? "border-r bg-background" : "w-0"
          )}
          style={{ width: state.isExpanded ? `${state.sidebarWidth}px` : '0px' }}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex flex-col gap-3 p-3 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{state.sessionTitle || 'New Chat'}</h3>
                <div className="flex items-center gap-1">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={handleNewChat}
                          disabled={state.isLoading || state.isExecuting}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>Start new chat</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => setShowSettings(true)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>Chat settings & statistics</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              
              {/* Agent Selection */}
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                  <SelectTrigger 
                    className="flex-1 h-8" 
                    title={sidebarAgents.find(agent => agent.name === selectedAgent)?.description || "Select an agent"}
                  >
                    <SelectValue placeholder="Select agent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(isLoadingAgents || isLoadingSettings) ? (
                      <SelectItem value="loading" disabled>
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Loading agents...
                        </div>
                      </SelectItem>
                    ) : sidebarAgents.length === 0 ? (
                      <SelectItem value="no-agents" disabled>
                        No agents available
                      </SelectItem>
                    ) : (

                      sidebarAgents.map((agent) => (
                        <SelectItem key={agent.id} value={agent.name} title={agent.description || ""}>
                          <span className="font-medium">{agent.displayName}</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Session Plan Display (collapsible header) */}
            {state.plan && state.plan.tasks && state.plan.tasks.length > 0 && (
              <ChatPlanDisplay 
                plan={state.plan} 
                mode="header" 
                defaultExpanded={true}
              />
            )}
            
            {/* Chat Messages */}
            <ScrollArea ref={scrollAreaRef} className="flex-1 p-3">
              <div className="space-y-4">
                {showWelcomeMessage && (
                  <div className="flex flex-col max-w-[95%] rounded-lg p-3 bg-muted text-muted-foreground">
                    <MarkdownRenderer 
                      content={welcomeMessage.content} 
                      compact={true}
                      className="text-sm"
                    />
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-70">
                        {welcomeMessage.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                )}
                {state.messages.map((message, index) => {
                  // Create a unique key using UUID, id, or fallback to index with timestamp
                  // This prevents duplicate key warnings
                  const messageKey = message.uuid || message.id || `msg-${index}-${message.timestamp?.getTime() || Date.now()}`;
                  
                  return (
                  <div 
                    key={message.id || index} 
                    className={cn(
                      "flex flex-col max-w-[95%] rounded-lg p-3",
                      message.role === 'user' ? 
                        "bg-primary text-primary-foreground ml-auto" : 
                        "bg-muted text-muted-foreground"
                    )}
                  >
                    {/* Tool Calls Display */}
                    {message.toolCalls && message.toolCalls.length > 0 && (
                      <ToolCallsList 
                        toolCalls={message.toolCalls} 
                        isStreaming={message.isStreaming}
                      />
                    )}
                    
                    {/* Sub-Agent Calls Display */}
                    {message.subAgentCalls && message.subAgentCalls.length > 0 && (
                      <SubAgentCallsList calls={message.subAgentCalls} />
                    )}
                    
                    {/* Entity Links Display */}
                    {message.entityLinks && message.entityLinks.length > 0 && (
                      <EntityLinksDisplay 
                        entityLinks={message.entityLinks}
                        animateNew={message.isStreaming}
                        onNavigate={onNavigate}
                      />
                    )}
                    
                    {/* Action Plan Display */}
                    {message.actionPlan && (
                      <div className="mt-3">
                        <PlanList plan={message.actionPlan} />
                      </div>
                    )}
                    
                    {/* Message content - displayed last so it's easily readable at the bottom */}
                    <div className="mt-3">
                      {message.role === 'assistant' ? (
                        <MarkdownRenderer 
                          content={message.content} 
                          compact={true}
                          className="text-sm"
                        />
                      ) : (
                        <div className="whitespace-pre-wrap text-sm">
                          {message.content}
                        </div>
                      )}
                    </div>
                    
                    {/* Message metadata */}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs opacity-70">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {message.metadata && (
                        <div className="flex items-center gap-2 text-xs opacity-70">
                          {message.metadata.tokenCount && (
                            <span title={`Input: ${message.metadata.tokenCount.input}, Output: ${message.metadata.tokenCount.output}`}>
                              {message.metadata.tokenCount.total} tokens
                            </span>
                          )}
                          {message.metadata.processingTime && (
                            <span>{(message.metadata.processingTime / 1000).toFixed(1)}s</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
                {state.isLoading && (
                  <div className="flex items-center gap-2 bg-muted text-muted-foreground rounded-lg p-3 max-w-[85%]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Working...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            
            {/* Input Area */}
            <div className="p-3 border-t border-slate-200 dark:border-slate-700">
              <div className="space-y-2">
                {/* File attachment display */}
                {selectedFile && (
                  <div className="flex justify-start">
                    <FileUpload
                      onFileSelect={handleFileSelect}
                      selectedFile={selectedFile}
                      disabled={state.isLoading || state.isExecuting}
                      className="max-w-xs"
                    />
                  </div>
                )}
                
                <div className="flex gap-2">
                  <Textarea
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask the Velocity Assistant..."
                    className="min-h-[60px] max-h-[120px] resize-none flex-1 py-2 px-3"
                    disabled={state.isLoading || state.isExecuting}
                  />
                  <div className="flex flex-col gap-2">
                    {/* File upload button */}
                    {!selectedFile && (
                      <FileUpload
                        onFileSelect={handleFileSelect}
                        selectedFile={selectedFile}
                        disabled={state.isLoading || state.isExecuting}
                      />
                    )}
                    
                    {/* Send/Stop button */}
                    {state.canTerminateExecution && state.currentExecutionId ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              onClick={handleStopExecution}
                              size="icon"
                              variant="destructive"
                              className="h-8 w-8"
                              aria-label="Stop execution"
                            >
                              <Square className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <p>Stop current execution</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Button 
                        onClick={handleSendMessage}
                        disabled={inputValue.trim() === '' || state.isLoading || state.isExecuting}
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Send message"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Resize Handle */}
          {state.isExpanded && (
            <div
              ref={resizeRef}
              className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-border/50 hover:bg-border transition-colors opacity-0 hover:opacity-100"
              onMouseDown={handleMouseDown}
              style={{ 
                background: isResizing ? 'rgb(var(--border))' : undefined,
                opacity: isResizing ? 1 : undefined 
              }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 right-1/2 translate-x-1/2">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
      
      {/* Chat Settings Modal */}
      <ChatSettingsModal
        open={showSettings}
        onOpenChange={setShowSettings}
        messages={state.messages}
        currentAgent={selectedAgent}
        sessionId={state.honchoSessionId}
        onResetChat={resetChat}
      />
    </div>
  );
} 